/**
 * How a document's type, size and dates are shown: by `DocumentRow` and the
 * pages that show a document outside a list.
 *
 * Kept out of the component's module so that editing the component keeps
 * Fast Refresh: a module that exports both components and other values loses
 * it.
 */
import {
  faBoxArchive,
  faFile,
  faFileCertificate,
  faFileCode,
  faFileCsv,
  faFileImage,
  faFileLines,
  faFilePdf,
  faFileZipper,
} from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export function iconForContentType(contentType?: string): IconDefinition {
  if (!contentType) return faFile;
  if (contentType.startsWith('image/')) return faFileImage;
  if (contentType === 'application/pdf') return faFilePdf;
  if (contentType === 'text/csv' || contentType === 'application/csv') return faFileCsv;
  if (contentType === 'application/x-brew-archive') return faBoxArchive;
  if (
    contentType === 'application/zip' ||
    contentType === 'application/x-tar' ||
    contentType === 'application/gzip' ||
    contentType === 'application/x-7z-compressed' ||
    contentType === 'application/x-rar-compressed'
  ) {
    return faFileZipper;
  }
  if (
    contentType.includes('x509') ||
    contentType.includes('pkcs') ||
    contentType.includes('certificate')
  ) {
    return faFileCertificate;
  }
  if (
    contentType.includes('json') ||
    contentType.includes('xml') ||
    contentType.includes('yaml') ||
    contentType.includes('javascript') ||
    contentType.includes('typescript') ||
    contentType.includes('html')
  ) {
    return faFileCode;
  }
  if (contentType.startsWith('text/')) return faFileLines;
  return faFile;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
